// @ts-nocheck
import React from 'react';
import { Button as AntButton } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';
import { colors, spacing, borderRadius, shadows } from './tokens';
import { getTypographyStyles } from './typography';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'warning';
export type ButtonSize = 'small' | 'medium' | 'large';

export interface ButtonProps extends Omit<AntButtonProps, 'type' | 'size'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  children: React.ReactNode;
  as?: string;
  href?: string;
}

const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
  small: {
    padding: `${spacing.xs} ${spacing.md}`,
    fontSize: '14px',
    height: '32px',
  },
  medium: {
    padding: `${spacing.sm} ${spacing.lg}`,
    fontSize: '16px',
    height: '40px',
  },
  large: {
    padding: `${spacing.md} ${spacing.xl}`,
    fontSize: '18px',
    height: '48px',
  },
};

const variantStyles: Record<ButtonVariant, {
  default: React.CSSProperties;
  hover?: React.CSSProperties;
  active?: React.CSSProperties;
}> = {
  primary: {
    default: {
      backgroundColor: colors.status.positive,
      color: colors.text.positive.onPositive,
      border: `1px solid ${colors.status.positive}`,
      boxShadow: shadows.sm,
    },
    hover: {
      backgroundColor: '#147A4B', // Darker shade of positive
      borderColor: '#147A4B',
    },
  },
  secondary: {
    default: {
      backgroundColor: colors.tertiary, // Teal #179DB5
      color: '#FFFFFF', // White text on teal
      border: `1px solid ${colors.tertiary}`,
      boxShadow: shadows.sm,
    },
    hover: {
      backgroundColor: '#1489A0', // Darker shade of tertiary teal
      borderColor: '#1489A0',
    },
  },
  outline: {
    default: {
      backgroundColor: 'transparent',
      color: colors.text.primary,
      border: `1px solid ${colors.border.default}`,
    },
    hover: {
      borderColor: colors.border.hover,
      backgroundColor: colors.background.secondary,
    },
  },
  ghost: {
    default: {
      backgroundColor: 'transparent',
      color: colors.text.primary,
      border: '1px solid transparent',
    },
    hover: {
      backgroundColor: colors.background.secondary,
    },
  },
  danger: {
    default: {
      backgroundColor: colors.status.negative,
      color: colors.text.danger.onDanger,
      border: `1px solid ${colors.status.negative}`,
      boxShadow: shadows.sm,
    },
    hover: {
      backgroundColor: '#A62322', // Darker shade of negative
      borderColor: '#A62322',
    },
  },
  warning: {
    default: {
      backgroundColor: colors.status.warning,
      color: colors.text.warning.onWarning,
      border: `1px solid ${colors.status.warning}`,
      boxShadow: shadows.sm,
    },
    hover: {
      backgroundColor: '#D4A017', // Darker shade of warning
      borderColor: '#D4A017',
    },
  },
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((
  {
    variant = 'primary',
    size = 'medium',
    fullWidth = false,
    children,
    className = '',
    style,
    disabled,
    as,
    href,
    ...restProps
  },
  ref
) => {
  const variantStyle = variantStyles[variant];
  const sizeStyle = sizeStyles[size];
  
  // Map our variants to Ant Design button types
  const getAntButtonType = (): AntButtonProps['type'] => {
    switch (variant) {
      case 'primary':
      case 'secondary':
      case 'danger':
      case 'warning':
        return 'primary';
      case 'outline':
        return 'default';
      case 'ghost':
        return 'text';
      default:
        return 'primary';
    }
  };
  
  const buttonStyle: React.CSSProperties = {
    ...variantStyle.default,
    ...sizeStyle,
    ...getTypographyStyles('button'),
    borderRadius: borderRadius.md,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    transition: 'all 0.2s ease',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    width: fullWidth ? '100%' : 'auto',
    ...style,
  };
  
  return (
    <AntButton
      ref={ref}
      type={getAntButtonType()}
      className={`atlas-button atlas-button-${variant} atlas-button-${size} ${className}`}
      style={buttonStyle}
      disabled={disabled}
      href={as === 'a' ? href : undefined}
      onMouseEnter={(e) => {
        if (!disabled && variantStyle.hover) {
          Object.assign(e.currentTarget.style, variantStyle.hover);
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled) {
          Object.assign(e.currentTarget.style, variantStyle.default);
        }
      }}
      {...restProps}
    >
      {children}
    </AntButton>
  );
});

// Icon button variant
export interface IconButtonProps extends ButtonProps {
  icon: React.ReactNode;
  'aria-label': string;
}

export const IconButton: React.FC<IconButtonProps> = ({
  icon,
  size = 'medium',
  ...restProps
}) => {
  const iconSizes: Record<ButtonSize, string> = {
    small: '32px',
    medium: '40px',
    large: '48px',
  };
  
  return (
    <Button
      {...restProps}
      size={size}
      style={{
        width: iconSizes[size],
        height: iconSizes[size],
        padding: 0,
        minWidth: 'unset',
        ...restProps.style,
      }}
    >
      {icon}
    </Button>
  );
};